import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  Users, AlertTriangle, Clock, CheckCircle, Activity,
  TrendingUp, Package, Shield, ChevronDown, ChevronUp,
  Mail, XCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTIVITY_LABELS = {
  active_today: { label: 'Activo hoy', color: 'bg-green-100 text-green-800 border-green-200' },
  active_recent: { label: 'Reciente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  inactive: { label: 'Inactivo', color: 'bg-red-100 text-red-800 border-red-200' },
};

const ALERT_COLORS = {
  green: 'border-l-green-500',
  yellow: 'border-l-amber-500',
  red: 'border-l-red-500',
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [roleFilter, setRoleFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (roleFilter !== 'all') params.role_filter = roleFilter;
      const res = await axios.get(`${API}/employee-dashboard/overview`, { params });
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [days, roleFilter]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
    </div>
  );

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-['Barlow_Condensed'] font-bold text-slate-800 tracking-tight">Panel de Empleados</h1>
          <p className="text-sm text-slate-500 mt-1">Actividad, rendimiento y control operativo</p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <Label className="text-xs text-slate-500">Periodo</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32" data-testid="period-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Rol</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-36" data-testid="role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="mechanic">Mecánico</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50" data-testid="card-total">
          <CardContent className="p-4 text-center">
            <Users size={24} className="mx-auto text-slate-400 mb-1" />
            <p className="text-2xl font-bold font-['Barlow_Condensed']">{s.total_employees}</p>
            <p className="text-xs text-slate-500">Total Empleados</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200" data-testid="card-active">
          <CardContent className="p-4 text-center">
            <CheckCircle size={24} className="mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold font-['Barlow_Condensed'] text-green-700">{s.active}</p>
            <p className="text-xs text-green-600">Correctos</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200" data-testid="card-warning">
          <CardContent className="p-4 text-center">
            <AlertTriangle size={24} className="mx-auto text-amber-500 mb-1" />
            <p className="text-2xl font-bold font-['Barlow_Condensed'] text-amber-700">{s.warning}</p>
            <p className="text-xs text-amber-600">Atención</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200" data-testid="card-critical">
          <CardContent className="p-4 text-center">
            <XCircle size={24} className="mx-auto text-red-500 mb-1" />
            <p className="text-2xl font-bold font-['Barlow_Condensed'] text-red-700">{s.critical}</p>
            <p className="text-xs text-red-600">Problema</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {data?.alerts?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-500" />
              Alertas Activas
              <Badge className="bg-red-600 text-white ml-1">{data.alerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {data.alerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                  a.level === 'red' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
                }`}>
                  {a.level === 'red' ? <XCircle size={14} /> : <AlertTriangle size={14} />}
                  <span className="flex-1">{a.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee List */}
      <div className="space-y-3">
        {data?.employees?.map(emp => {
          const actLabel = ACTIVITY_LABELS[emp.activity_status] || ACTIVITY_LABELS.inactive;
          const isExpanded = expanded[emp.id];
          return (
            <Card key={emp.id} className={`border-l-4 ${ALERT_COLORS[emp.alert_level] || 'border-l-slate-300'} hover:shadow-md transition-shadow`} data-testid={`employee-${emp.id}`}>
              <CardContent className="p-4">
                {/* Top row */}
                <div className="flex items-center justify-between cursor-pointer" onClick={() => toggle(emp.id)}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      emp.alert_level === 'red' ? 'bg-red-100' : emp.alert_level === 'yellow' ? 'bg-amber-100' : 'bg-green-100'
                    }`}>
                      <span className="text-sm font-bold">{(emp.name || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{emp.name}</p>
                      <p className="text-xs text-slate-400">{emp.email} · {emp.role_name || emp.role}</p>
                    </div>
                    <Badge className={`${actLabel.color} border text-xs ml-2`}>{actLabel.label}</Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    {/* Quick metrics */}
                    <div className="hidden md:flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-lg font-bold font-['Barlow_Condensed']">{emp.orders_created}</p>
                        <p className="text-[10px] text-slate-400 uppercase">Creados</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold font-['Barlow_Condensed'] text-green-600">{emp.orders_completed}</p>
                        <p className="text-[10px] text-slate-400 uppercase">Complet.</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold font-['Barlow_Condensed']">{emp.active_assigned}</p>
                        <p className="text-[10px] text-slate-400 uppercase">Activos</p>
                      </div>
                      {emp.urgent_pending > 0 && (
                        <Badge className="bg-red-600 text-white text-xs">{emp.urgent_pending} urgente</Badge>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                    {/* Alert reasons */}
                    {emp.alert_reasons.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {emp.alert_reasons.map((r, i) => (
                          <Badge key={i} className={`text-xs ${emp.alert_level === 'red' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'} border`}>{r}</Badge>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Activity */}
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1"><Activity size={10} /> Actividad</p>
                        <p className="text-sm mt-1">
                          <strong>Última conexión:</strong><br />
                          {emp.last_login ? new Date(emp.last_login).toLocaleString('es-ES') : 'Nunca'}
                        </p>
                        {emp.last_action && (
                          <p className="text-xs text-slate-500 mt-2">
                            <strong>Última acción:</strong><br />
                            {emp.last_action}
                          </p>
                        )}
                      </div>

                      {/* Performance */}
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1"><TrendingUp size={10} /> Rendimiento ({days}d)</p>
                        <div className="mt-1 space-y-1 text-sm">
                          <div className="flex justify-between"><span>Creados</span><strong>{emp.orders_created}</strong></div>
                          <div className="flex justify-between"><span>Completados</span><strong className="text-green-600">{emp.orders_completed}</strong></div>
                          <div className="flex justify-between"><span>Cancelados</span><strong className="text-red-500">{emp.orders_cancelled}</strong></div>
                          <div className="flex justify-between"><span>T. medio</span><strong>{emp.avg_resolution_hours ? `${emp.avg_resolution_hours}h` : '—'}</strong></div>
                        </div>
                      </div>

                      {/* Errors */}
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1"><Shield size={10} /> Control</p>
                        <div className="mt-1 space-y-1 text-sm">
                          <div className="flex justify-between"><span>Eliminados SA</span><strong>{emp.deleted_by_admin}</strong></div>
                          <div className="flex justify-between"><span>Sin completar</span><strong className={emp.orders_incomplete > 5 ? 'text-amber-600' : ''}>{emp.orders_incomplete}</strong></div>
                        </div>
                      </div>

                      {/* Workload */}
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1"><Package size={10} /> Carga actual</p>
                        <div className="mt-1 space-y-1 text-sm">
                          <div className="flex justify-between"><span>Asignados</span><strong className={emp.active_assigned > 10 ? 'text-amber-600' : ''}>{emp.active_assigned}</strong></div>
                          <div className="flex justify-between"><span>Urgentes</span><strong className={emp.urgent_pending > 0 ? 'text-red-600' : ''}>{emp.urgent_pending}</strong></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {(!data?.employees || data.employees.length === 0) && (
          <Card><CardContent className="p-8 text-center text-slate-400">No hay empleados registrados</CardContent></Card>
        )}
      </div>
    </div>
  );
}
